import React from "react";
import { Badge, Box, DialogTitle, Text } from "@salestrak/ui";

import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { isInvited, isSuspended } from "#app/lib/role-utils";
import { usePresence, type PresenceStatus } from "#app/lib/data/presence-provider";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import {
  resolveEntityLabel,
  teamRefToEntity,
  type EntityData,
} from "#app/lib/entity-visuals";

import { EntityTag } from "./entity-tag";
import { EntityGroupTag } from "./entity-group";
import { DetailFieldRow } from "./detail-field-row";
import { EntityDetailShell, EntityDetailCard } from "./entity-detail-shell";
import { statusBadgeVariant } from "./badge-tone";
import type { Omittable } from "@salestrak/ui";

// Chip tone per live status. Unknown (first poll in flight) reads as
// Offline — the pessimistic default matches the avatar dots' server-confirmed
// contract without leaving the chip blank.
const PRESENCE_CHIP: Record<
  PresenceStatus,
  { label: string; color: "success" | "warning" | "muted" }
> = {
  ONLINE: { label: "Online", color: "success" },
  AWAY: { label: "Away", color: "warning" },
  OFFLINE: { label: "Offline", color: "muted" },
};

type MemberProfileDialogProps = Omittable<{
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditProfile?: (userId: string) => void;
}>;

export function MemberProfileDialog({ userId, open, onOpenChange, onEditProfile }: MemberProfileDialogProps) {
  const { user, member } = useMemberDetail(userId ?? undefined, { skip: !open });

  const entity: EntityData = {
    kind: "user",
    id: userId ?? "unknown",
    user: user ?? null,
  };
  const displayName = resolveEntityLabel(entity);
  const roleName = member?.roles?.[0]?.roleName ?? "None";
  const teams = member?.teams ?? [];
  const suspended = isSuspended(user);
  const invited = isInvited(user);
  const presence = usePresence(userId);
  const presenceChip = PRESENCE_CHIP[presence?.status ?? "OFFLINE"];

  // "Last online …": live "now" while online; suspended/invited members (and
  // anyone with no heartbeat stamp) read "never"; otherwise the relative
  // last_online_at stamp. Lowercased so mid-sentence casing holds
  // ("yesterday", "just now").
  const lastOnlineLabel =
    presence?.status === "ONLINE"
      ? "Last online now"
      : suspended || invited || !user?.lastOnlineAt
        ? "Last online never"
        : `Last online ${formatRelativeDate(user.lastOnlineAt, "long").toLowerCase()}`;

  const header = (
    <Box direction="row" align="center" gap="md">
      <EntityTag entity={entity} display="visual" size="lg" interactive={false} />
      <Box gap="xs">
        <Box direction="row" align="baseline" gap="sm">
          <DialogTitle>
            <Text size="lg" weight="semibold" variant={suspended ? "muted" : undefined}>
              {displayName}
            </Text>
          </DialogTitle>
          <Text size="sm" variant="muted">
            {roleName}
          </Text>
        </Box>
        <Box direction="row" align="center" gap="sm">
          <Badge size="sm" variant={statusBadgeVariant(presenceChip.color)}>
            {presenceChip.label}
          </Badge>
          <Text size="xs" variant="muted">
            {lastOnlineLabel}
          </Text>
        </Box>
      </Box>
    </Box>
  );

  const factsCard = (
    <EntityDetailCard>
      <Box display="grid" columns={1} gap="lg" className="sm:grid-cols-2">
        <DetailFieldRow icon="circle-user" label="Username" value={user?.username} copyable />
        <DetailFieldRow icon="mail" label="Email" value={user?.email} link="email" truncate={1} />
        <DetailFieldRow icon="badge-check" label="Title" value={user?.title} copyable />
        <DetailFieldRow icon="phone" label="Phone" value={user?.phoneNumber} link="phone" />

        <DetailFieldRow icon="users" label="Teams" align="start">
          {teams.length > 0 ? (
            <EntityGroupTag
              entities={teams.map(teamRefToEntity)}
              variant="expanded"
              size="sm"
              title="Teams"
            />
          ) : (
            <Text size="sm" variant="muted">
              None
            </Text>
          )}
        </DetailFieldRow>
      </Box>
    </EntityDetailCard>
  );

  const canEdit = onEditProfile && userId;

  return (
    <EntityDetailShell
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      srDescription={`${displayName} profile`}
      body={factsCard}
      onEdit={canEdit ? () => onEditProfile!(userId!) : undefined}
      editLabel="Edit profile"
    />
  );
}
